/**
 * <featured-product-card> — a hover latch for the featured-product cards.
 *
 * WHY THIS EXISTS AT ALL. The hover — the white square dissolving behind the piece —
 * is plain CSS `:hover`, and in every browser but one that is enough. On Windows Edge
 * with a mouse, the state was measured switching itself OFF after a moment while the
 * cursor sat still on the card. Chromium re-tests hover against a stationary cursor
 * after layout and scroll changes, and Lenis keeps a rAF loop running, so a briefly
 * wrong re-test is the likely trigger. CSS cannot defend against a browser deciding
 * the pointer has left; this can.
 *
 * WHAT IT DOES. Sets `data-hovered` on the card's `group/card` element when a mouse
 * or pen enters, and clears it only when the pointer is PROVABLY outside — the leave
 * event's own coordinates are checked against the card's box. A false leave keeps the
 * state and waits for the first real pointer move outside. The CSS keeps its native
 * `group-hover` too, so if this file fails to load the card behaves exactly as before.
 *
 * Touch is ignored: a touch "enter" is a tap, and the card is a link — the page is
 * already leaving. No `(hover: hover)` query either; the pointer type is the honest
 * signal, and Windows machines are known to misreport hover capability.
 *
 * A REAL ASSET, NOT A `{% javascript %}` BLOCK — see the note at the top of
 * assets/film-strip.js. Touches no shared state, so load order does not matter.
 */

if (!customElements.get("featured-product-card")) {
  customElements.define(
    "featured-product-card",
    class FeaturedProductCard extends HTMLElement {
      connectedCallback() {
        this.card = this.firstElementChild;
        if (!this.card) return;

        this.handleEnter = this.handleEnter.bind(this);
        this.handleLeave = this.handleLeave.bind(this);
        this.handleDocumentMove = this.handleDocumentMove.bind(this);
        this.release = this.release.bind(this);

        this.card.addEventListener("pointerenter", this.handleEnter);
        this.card.addEventListener("pointerleave", this.handleLeave);
        // Alt-tab with the cursor on a card: no leave event ever comes.
        window.addEventListener("blur", this.release);
      }

      disconnectedCallback() {
        this.card?.removeEventListener("pointerenter", this.handleEnter);
        this.card?.removeEventListener("pointerleave", this.handleLeave);
        window.removeEventListener("blur", this.release);
        this.release();
      }

      handleEnter(event) {
        if (event.pointerType === "touch") return;
        this.stopWatching();
        this.card.setAttribute("data-hovered", "");
      }

      handleLeave(event) {
        if (event.pointerType === "touch") return;

        if (this.isOutside(event.clientX, event.clientY)) {
          this.release();
          return;
        }

        // A leave with the pointer still inside the card: the browser's mistake, not
        // the visitor's. Hold the state until a real move proves otherwise.
        if (!this.watching) {
          this.watching = true;
          document.addEventListener("pointermove", this.handleDocumentMove);
        }
      }

      handleDocumentMove(event) {
        if (this.isOutside(event.clientX, event.clientY)) this.release();
      }

      isOutside(x, y) {
        const box = this.card.getBoundingClientRect();
        return x < box.left || x > box.right || y < box.top || y > box.bottom;
      }

      release() {
        this.stopWatching();
        this.card?.removeAttribute("data-hovered");
      }

      stopWatching() {
        if (!this.watching) return;
        this.watching = false;
        document.removeEventListener("pointermove", this.handleDocumentMove);
      }
    },
  );
}
